import {
  ASSET_UPLOAD_MAX_BYTES,
  AssetErrorResponse,
  ConfirmAssetUploadResponse,
  SignAssetUploadResponse,
  ValidationErrorResponse,
  type AdminAsset,
  type SignAssetUploadRequest,
} from '@diana/contracts';

export type MediaUploadStatus =
  'Selection' | 'Preparing' | 'Signing' | 'Uploading' | 'Confirming' | 'Ready' | 'Error';

export type UploadFile = Blob & { readonly name: string };

export type MediaSelectionInput = {
  file: UploadFile;
  alt: string;
};

export type PreparedImage = {
  checksum: string;
  width: number;
  height: number;
};

export type MediaUploadSelection = {
  filename: string;
  contentType: 'image/jpeg' | 'image/webp';
  byteSize: number;
  alt: string;
};

export type MediaUploadSnapshot = {
  status: MediaUploadStatus;
  selection: MediaUploadSelection | null;
  progress: number;
  error: Error | null;
  asset: AdminAsset | null;
};

type ResponseSchema<T> = { parse(value: unknown): T };

export type MediaUploadApi = {
  getMe(): Promise<{ csrfToken: string }>;
  request(
    path: string,
    request: {
      method: 'POST';
      body: unknown;
      schema: ResponseSchema<unknown>;
    },
  ): Promise<unknown>;
};

export type RawUploadInput = {
  url: string;
  file: Blob;
  headers: Record<string, string>;
  signal: AbortSignal;
  timeoutMs: number;
  onProgress(loaded: number, total: number): void;
};

type MediaUploadOptions = {
  api: MediaUploadApi;
  prepare?: (file: Blob) => Promise<PreparedImage>;
  upload?: (input: RawUploadInput) => Promise<void>;
  invalidate?: () => void | Promise<void>;
  maxBytes?: number;
  maxWidth?: number;
  maxHeight?: number;
  uploadTimeoutMs?: number;
};

const DEFAULT_MAX_DIMENSION = 12_000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 90_000;
const MAX_UPLOAD_TIMEOUT_MS = 120_000;
const checksumPattern = /^[0-9a-f]{64}$/;
const plainFilenamePattern = /^[^/\\\u0000-\u001f\u007f]+$/;
const proxyPathPattern =
  /^\/v1\/admin\/uploads\/proxy\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export function createMediaUploadController(options: MediaUploadOptions) {
  const prepare = options.prepare ?? prepareImage;
  const upload = options.upload ?? xhrUpload;
  const maxBytes = options.maxBytes ?? ASSET_UPLOAD_MAX_BYTES;
  const maxWidth = options.maxWidth ?? DEFAULT_MAX_DIMENSION;
  const maxHeight = options.maxHeight ?? DEFAULT_MAX_DIMENSION;
  const uploadTimeoutMs = boundedTimeout(options.uploadTimeoutMs ?? DEFAULT_UPLOAD_TIMEOUT_MS);
  const listeners = new Set<() => void>();
  let selected: { input: MediaSelectionInput; public: MediaUploadSelection } | null = null;
  let generation = 0;
  let inFlight: Promise<void> | null = null;
  let inFlightGeneration = -1;
  let activeAttempt: AbortController | null = null;
  let disposed = false;
  let snapshot: MediaUploadSnapshot = {
    status: 'Selection',
    selection: null,
    progress: 0,
    error: null,
    asset: null,
  };

  function publish(next: Partial<MediaUploadSnapshot>) {
    if (disposed) return;
    snapshot = { ...snapshot, ...next };
    for (const listener of listeners) listener();
  }

  function isCurrent(attempt: number) {
    return !disposed && generation === attempt;
  }

  async function runAttempt(
    attempt: number,
    current: { input: MediaSelectionInput; public: MediaUploadSelection },
    signal: AbortSignal,
  ) {
    publish({ status: 'Preparing', progress: 0, error: null, asset: null });
    try {
      const prepared = await prepare(current.input.file);
      if (!isCurrent(attempt)) return;
      assertPrepared(prepared, maxWidth, maxHeight);

      const signBody: SignAssetUploadRequest = {
        filename: current.public.filename,
        contentType: current.public.contentType,
        byteSize: current.public.byteSize,
        checksum: prepared.checksum,
        width: prepared.width,
        height: prepared.height,
        alt: current.public.alt,
      };
      publish({ status: 'Signing' });
      const signed = SignAssetUploadResponse.parse(
        await options.api.request('/api/v1/admin/uploads/sign', {
          method: 'POST',
          body: signBody,
          schema: SignAssetUploadResponse,
        }),
      );
      if (!isCurrent(attempt)) return;

      const headers = uploadHeaders(
        signed.upload.requiredHeaders,
        current.input.file,
        prepared.checksum,
      );
      const target = uploadTarget(signed.upload.mode, signed.upload.uploadUrl, signed.upload.nonce);
      if (signed.upload.mode === 'Proxy') {
        const me = await options.api.getMe();
        if (!isCurrent(attempt)) return;
        headers['x-diana-csrf'] = me.csrfToken;
      }

      let progress = 0;
      publish({ status: 'Uploading', progress });
      await upload({
        url: target,
        file: current.input.file,
        headers,
        signal,
        timeoutMs: uploadTimeoutMs,
        onProgress(loaded, total) {
          if (!isCurrent(attempt)) return;
          const denominator = total > 0 ? total : current.input.file.size;
          const next = Math.max(0, Math.min(100, Math.round((loaded / denominator) * 100)));
          if (next <= progress) return;
          progress = next;
          publish({ status: 'Uploading', progress });
        },
      });
      if (!isCurrent(attempt)) return;

      publish({ status: 'Confirming', progress: 100 });
      const confirmed = ConfirmAssetUploadResponse.parse(
        await options.api.request('/api/v1/admin/uploads/confirm', {
          method: 'POST',
          body: {
            assetId: signed.upload.assetId,
            nonce: signed.upload.nonce,
            objectKey: signed.upload.objectKey,
            checksum: prepared.checksum,
          },
          schema: ConfirmAssetUploadResponse,
        }),
      );
      if (!isCurrent(attempt)) return;
      if (
        !confirmedAssetMatches({
          asset: confirmed.asset,
          assetId: signed.upload.assetId,
          selection: current.public,
          prepared,
        })
      ) {
        throw new Error('Upload confirmation does not match the signed image');
      }
      try {
        await options.invalidate?.();
      } catch {
        // Cache refresh is best-effort; the confirmed immutable asset remains ready.
      }
      if (!isCurrent(attempt)) return;
      publish({ status: 'Ready', progress: 100, asset: confirmed.asset, error: null });
    } catch (caught) {
      if (!isCurrent(attempt)) return;
      publish({
        status: 'Error',
        error: caught instanceof Error ? caught : new Error('Media upload failed'),
        asset: null,
      });
    }
  }

  function start() {
    if (disposed || !selected) return Promise.resolve();
    if (inFlight && inFlightGeneration === generation) return inFlight;
    if (snapshot.status !== 'Selection' && snapshot.status !== 'Error') return Promise.resolve();
    const attempt = generation;
    const current = selected;
    const attemptController = new AbortController();
    activeAttempt = attemptController;
    const run = runAttempt(attempt, current, attemptController.signal).finally(() => {
      if (inFlight === run) {
        inFlight = null;
        inFlightGeneration = -1;
      }
      if (activeAttempt === attemptController) activeAttempt = null;
    });
    inFlight = run;
    inFlightGeneration = attempt;
    return run;
  }

  return {
    getSnapshot() {
      return snapshot;
    },
    subscribe(listener: () => void) {
      if (disposed) return () => {};
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    select(input: MediaSelectionInput) {
      if (disposed || snapshot.status === 'Confirming') return false;
      activeAttempt?.abort();
      generation += 1;
      try {
        selected = validatedSelection(input, maxBytes);
        publish({
          status: 'Selection',
          selection: selected.public,
          progress: 0,
          error: null,
          asset: null,
        });
        return true;
      } catch (caught) {
        selected = null;
        publish({
          status: 'Error',
          selection: null,
          progress: 0,
          error: caught instanceof Error ? caught : new Error('Invalid image selection'),
          asset: null,
        });
        return false;
      }
    },
    start,
    retry() {
      return snapshot.status === 'Error' ? start() : Promise.resolve();
    },
    dispose() {
      disposed = true;
      activeAttempt?.abort();
      generation += 1;
      listeners.clear();
      selected = null;
    },
  };
}

export type MediaUploadController = ReturnType<typeof createMediaUploadController>;

function confirmedAssetMatches(input: {
  asset: AdminAsset;
  assetId: string;
  selection: MediaUploadSelection;
  prepared: PreparedImage;
}) {
  const { asset, assetId, selection, prepared } = input;
  const format = selection.contentType === 'image/jpeg' ? 'jpeg' : 'webp';
  return (
    asset.id === assetId &&
    asset.status === 'Ready' &&
    asset.sourceType === 'R2' &&
    asset.byteSize === selection.byteSize &&
    asset.width === prepared.width &&
    asset.height === prepared.height &&
    asset.format === format &&
    asset.mimeType === selection.contentType &&
    asset.originalFilename === selection.filename &&
    asset.checksum === prepared.checksum &&
    asset.alt === selection.alt
  );
}

function validatedSelection(
  input: MediaSelectionInput,
  maxBytes: number,
): { input: MediaSelectionInput; public: MediaUploadSelection } {
  const filename = input.file.name.trim();
  if (!filename || filename.length > 255 || !plainFilenamePattern.test(filename)) {
    throw new Error('Expected a plain image filename');
  }
  if (input.file.type !== 'image/jpeg' && input.file.type !== 'image/webp') {
    throw new Error('Only prepared JPEG and WebP images can be uploaded');
  }
  const extension = input.file.type === 'image/jpeg' ? /\.(?:jpe?g)$/i : /\.webp$/i;
  if (!extension.test(filename)) {
    throw new Error('Filename extension does not match the image type');
  }
  if (!Number.isInteger(input.file.size) || input.file.size <= 0 || input.file.size > maxBytes) {
    throw new Error('Image file size is outside the upload limit');
  }
  const alt = input.alt.trim();
  if (!alt || alt.length > 500) throw new Error('Image alt text is required');
  const publicSelection: MediaUploadSelection = {
    filename,
    contentType: input.file.type,
    byteSize: input.file.size,
    alt,
  };
  return {
    input: {
      file: input.file,
      alt,
    },
    public: publicSelection,
  };
}

function assertPrepared(prepared: PreparedImage, maxWidth: number, maxHeight: number) {
  if (!checksumPattern.test(prepared.checksum)) throw new Error('Prepared checksum is invalid');
  if (
    !Number.isInteger(prepared.width) ||
    !Number.isInteger(prepared.height) ||
    prepared.width <= 0 ||
    prepared.height <= 0 ||
    prepared.width > maxWidth ||
    prepared.height > maxHeight
  ) {
    throw new Error('Prepared image dimensions are invalid');
  }
}

function uploadHeaders(
  required: Record<string, string>,
  file: Blob,
  checksum: string,
): Record<string, string> {
  const allowed = new Set([
    'content-type',
    'content-length',
    'if-none-match',
    'x-amz-checksum-sha256',
    'x-amz-meta-sha256',
  ]);
  const normalized: Record<string, string> = {};
  for (const [name, value] of Object.entries(required)) {
    const lower = name.toLowerCase();
    if (!allowed.has(lower)) throw new Error('Signed upload contains an unexpected header');
    if (normalized[lower] !== undefined && normalized[lower] !== value) {
      throw new Error('Signed upload contains conflicting headers');
    }
    normalized[lower] = value;
  }
  if (normalized['content-length'] !== String(file.size)) {
    throw new Error('Signed upload length does not match the selected file');
  }
  if (normalized['content-type'] !== file.type) {
    throw new Error('Signed upload type does not match the selected file');
  }
  if (
    normalized['if-none-match'] !== '*' ||
    normalized['x-amz-checksum-sha256'] !== checksumBase64(checksum) ||
    normalized['x-amz-meta-sha256'] !== checksum
  ) {
    throw new Error('Signed upload constraints do not match the prepared image');
  }
  delete normalized['content-length'];
  return normalized;
}

function checksumBase64(checksum: string) {
  const bytes = new Uint8Array(checksum.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(checksum.slice(index * 2, index * 2 + 2), 16);
  }
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function uploadTarget(mode: 'Direct' | 'Proxy', url: string, nonce: string) {
  if (mode === 'Direct') {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      throw new Error('Direct upload URL must be absolute HTTPS');
    }
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password) {
      throw new Error('Direct upload URL must be absolute HTTPS');
    }
    return parsed.toString();
  }
  const match = proxyPathPattern.exec(url);
  if (!match || match[1]!.toLowerCase() !== nonce.toLowerCase()) {
    throw new Error('Proxy upload path does not match the signed nonce');
  }
  return `/api${url}`;
}

export async function prepareImage(file: Blob): Promise<PreparedImage> {
  const [bytes, bitmap] = await Promise.all([
    file.arrayBuffer(),
    createImageBitmap(file, { imageOrientation: 'from-image' }),
  ]);
  try {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return {
      checksum: [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join(''),
      width: bitmap.width,
      height: bitmap.height,
    };
  } finally {
    bitmap.close();
  }
}

export function xhrUpload(input: RawUploadInput): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;
    const finish = (outcome: () => void) => {
      if (settled) return;
      settled = true;
      input.signal.removeEventListener('abort', abort);
      outcome();
    };
    const abort = () => request.abort();
    request.open('PUT', input.url, true);
    request.timeout = boundedTimeout(input.timeoutMs);
    for (const [name, value] of Object.entries(input.headers)) {
      if (name.toLowerCase() !== 'content-length') request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => input.onProgress(event.loaded, event.total);
    request.onerror = () => finish(() => reject(new Error('Media upload network request failed')));
    request.onabort = () => finish(() => reject(new Error('Media upload was aborted')));
    request.ontimeout = () => finish(() => reject(new Error('Media upload timed out')));
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) finish(resolve);
      else finish(() => reject(proxyUploadError(request.status, request.responseText)));
    };
    input.signal.addEventListener('abort', abort, { once: true });
    if (input.signal.aborted) {
      abort();
      return;
    }
    request.send(input.file);
  });
}

function proxyUploadError(status: number, responseText: string) {
  const fallback = new Error(`Media upload failed with status ${status}`);
  if (!responseText) return fallback;
  let payload: unknown;
  try {
    payload = JSON.parse(responseText);
  } catch {
    return fallback;
  }
  for (const schema of [ValidationErrorResponse, AssetErrorResponse]) {
    const parsed = schema.safeParse(payload);
    if (!parsed.success) continue;
    const field = parsed.data.error.fields?.[0];
    if (field) return new Error(`${field.message} (${field.path})`);
    return new Error(parsed.data.error.message);
  }
  return fallback;
}

function boundedTimeout(timeoutMs: number) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new Error('Expected a positive media upload timeout');
  }
  return Math.min(Math.floor(timeoutMs), MAX_UPLOAD_TIMEOUT_MS);
}
